import React, {useCallback, useEffect, useRef} from "react";
import Box from "@mui/material/Box";
import {usePageContext} from "../../../../context/PageContext";
import {usePathname} from "next/navigation";
import routes from "../../../../common/constants/routes";
import MobileMenuHeader from "./components/MobileMenuHeader";
import {useMobileNavigation} from "./hooks/useMobileNavigation";
import MobileMenuSearch from "./components/MobileMenuSearch";
import MobileMenuDrawer from "./components/MobileMenuDrawer";
import TopNavigationItems from "./components/TopNavigationItems";
import NavigationLevelContent from "./components/NavigationLevelContent";
import {CmLinkableType} from "../../../types";

export interface MobileMenuProps {
    items: Array<CmLinkableType>;
    onClose: () => void;
    open: boolean;
    page: any;
    topNaviItems?: Array<CmLinkableType>;
    navigation: any;
    isEasyLangMode?: boolean;
}

const MobileMenu = (props: MobileMenuProps) => {
    const {searchEnabled} = usePageContext();
    const pathname = usePathname();
    const initialOpenRef = useRef(true);

    const {
        navigationLevel,
        activeCategory,
        activeSubcategory,
        navigateToFirstLevel,
        handleMenuItemClick,
        isItemActive,
        itemsToRender,
        totalItems,
        resetFromPage
    } = useMobileNavigation(props.items, props.page);

    const isTopNaviItemActive = useCallback((item: CmLinkableType, isEasyLangMode?: boolean) => {
        const children = (item as any)?.children as CmLinkableType[] | undefined;
        if (Array.isArray(children) && children.length > 0) return false;

        const current = (props.page?.selfUrl ?? '').replace(/\/+$/, '');
        const target = (item?.selfUrl ?? '').replace(/\/+$/, '');
        if (!target) return false;

        if (isEasyLangMode && current.startsWith(target + '/')) {
            // For now keep existing logic or decide if EasyLang affects TopNavi highlighting
        }

        return current === target || (!isEasyLangMode && current.startsWith(target + '/'));
    }, [props.page?.selfUrl]);

    const isAnyTopNaviItemActive = !!props.topNaviItems?.some((item) => isTopNaviItemActive(item, props.isEasyLangMode));


    // Check if any top navigation item is active and ensure first level is opened on initial menu opening
    useEffect(() => {
        if (props.open && props.topNaviItems && props.topNaviItems.length > 0 &&
            initialOpenRef.current) {
            const anyTopActive = props.topNaviItems.some((item) => isTopNaviItemActive(item, props.isEasyLangMode));
            if (anyTopActive) {
                navigateToFirstLevel();
            }
            initialOpenRef.current = false;
        }

        if (!props.open) {
            initialOpenRef.current = true;
        }
    }, [props.open, props.topNaviItems, props.isEasyLangMode, isTopNaviItemActive, navigateToFirstLevel]);

    // When the mobile menu is opened, initialize the state from the current page
    // If a Top-Navi page is currently active, always show first level
    useEffect(() => {
        if (!props.open) return;
        if (isAnyTopNaviItemActive) {
            navigateToFirstLevel();
        } else {
            resetFromPage();
        }
    }, [props.open, isAnyTopNaviItemActive, resetFromPage, navigateToFirstLevel]);

    return (
        <MobileMenuDrawer open={props.open} onClose={props.onClose}>
            <MobileMenuSearch
                searchEnabled={searchEnabled}
                pathname={pathname}
                onClose={props.onClose}
            />

            <Box sx={{
                mt: searchEnabled && !(pathname?.indexOf(routes.SEARCH_RESULTS) > -1) ? 0 : '-16px'
            }}>
                <MobileMenuHeader
                    navigationLevel={navigationLevel}
                    activeCategory={activeCategory}
                    activeSubcategory={activeSubcategory}
                    onBackClick={navigateToFirstLevel}
                    onClose={props.onClose}
                />
            </Box>

            <Box sx={{overflow: 'auto'}}>
                <div data-cypress="mobileMenuContent">
                    <NavigationLevelContent
                        navigationLevel={navigationLevel}
                        itemsToRender={itemsToRender}
                        totalItems={totalItems}
                        isItemActive={(item) => (isAnyTopNaviItemActive ? false :
                            isItemActive(item, props.isEasyLangMode))}
                        onItemClick={handleMenuItemClick}
                        onClose={props.onClose}
                        navigation={props.navigation}
                        isEasyLangMode={props.isEasyLangMode}
                    />
                </div>

                {props.topNaviItems && (
                    <TopNavigationItems
                        items={props.topNaviItems}
                        onClose={props.onClose}
                        isItemActive={isTopNaviItemActive}
                        isEasyLangMode={props.isEasyLangMode}
                    />
                )}
            </Box>
        </MobileMenuDrawer>
    );
};

export default MobileMenu;
